import {FC, useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {OverlayTrigger, Tooltip, Modal} from 'react-bootstrap'
import {ID} from '../../../core/types'
import {deleteRole, updateRoleStatus} from '../../../core/_requests'
import {toast} from 'react-toastify'
import { checkPermission } from 'app/utils/checkPermission'
import { useQueryResponse } from '../../core/QueryResponseProvider'

type Props = {
  id: ID
  status: boolean
}

const RoleActionsCell: FC<Props> = ({id, status}) => {
  const navigate = useNavigate()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const {refetch} = useQueryResponse()

  const handleDeleteClick = async (id: ID) => {
    try {
      await deleteRole(id)
      toast.success('Role deleted successfully')
      refetch()
    } catch (error) {
      toast.error('Failed to delete role')
    }
  }

  const changeStatus = async (value: boolean) => {
    try {
      let response = await updateRoleStatus(id, { status: value })
      if (response.responseStatus == 200) {
        refetch()
        toast.success(response.responseMessage)
      }
      else {
        toast.error(response.responseMessage)
      }
    }
    catch (err: any) {
      let message = err?.message ? err.message : err?.responseMessage ? err.responseMessage : "Something went wrong" as any
      toast.error(message)
    }
  }

  return (
    <>
      <div className='d-flex justify-content-start flex-shrink-0'>
        {checkPermission('Roles', 'view')&&(
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip className='py-2 px-2 fs-5 '>View</Tooltip>}
          >
            <button
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
              onClick={() => navigate(`/role-management/roles/view/${id}`)}
            >
              <i className="fa-solid fa-file-lines text-primary fs-4"></i>
            </button>
          </OverlayTrigger>
        )}
        <OverlayTrigger
          placement='top'
          overlay={<Tooltip className='py-2 px-2 fs-5 '>{status ? 'Inactive' : 'Active'}</Tooltip>}
        >
          <button
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
            onClick={() => setShowStatusModal(true)}
          >
            <i className={`fa-solid fa-eye${status ? '' : '-slash'} fs-4 ${status ? 'text-primary' : 'text-danger'}`}></i>
          </button>
        </OverlayTrigger>
        {checkPermission('Roles', 'edit')&&(
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip className='py-2 px-2 fs-5 '>Edit</Tooltip>}
          >
            <button
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
              onClick={() => navigate(`/role-management/roles/edit/${id}`)}
            >
              <i className='fa-solid fa-user-pen fs-4 text-warning'></i>
            </button>
          </OverlayTrigger>
        )}
      </div>

      {/* Status Change Confirmation Modal */}
      <Modal 
        show={showStatusModal} 
        onHide={() => setShowStatusModal(false)}
        className="modal-dialog-centered"
        size="sm"
      >
        <Modal.Header className="border-0 pb-0">
          <div className="d-flex align-items-center w-100">
            <i className={`fa-solid fa-${status ? 'exclamation-triangle' : 'check-circle'} fs-2x ${status ? 'text-warning' : 'text-success'} me-3`}></i>
            <h5 className="mb-0">{status ? 'Inactivate Role' : 'Activate Role'}</h5>
          </div>
        </Modal.Header>
        <Modal.Body className="pt-4">
          {status ? (
            <div className="text-center">
              <i className="fa-solid fa-users-slash fs-2x text-warning mb-3"></i>
              <p className="mb-0">
                Inactivating this role will affect all users assigned to it. They will lose access to the system until the role is reactivated.
              </p>
            </div>
          ) : (
            <div className="text-center">
              <i className="fa-solid fa-users fs-2x text-success mb-3"></i>
              <p className="mb-0">
                Activating this role will restore access for all users assigned to it.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 d-flex justify-content-between">
          <div className="d-flex w-100 gap-2">
            <button
              className={`btn ${status ? 'btn-warning' : 'btn-success'} w-50`}
              onClick={() => {
                changeStatus(!status)
                setShowStatusModal(false)
              }}
            >
              {status ? 'Inactivate' : 'Activate'}
            </button>
            <button
              className="btn btn-light me-2 w-50"
              onClick={() => setShowStatusModal(false)}
            >
              No
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this role?</Modal.Body>
        <Modal.Footer>
          <button className='btn btn-light' onClick={() => setShowDeleteModal(false)}>
            Cancel
          </button>
          <button className='btn btn-danger' onClick={() => {
            handleDeleteClick(id)
            setShowDeleteModal(false)
          }}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {RoleActionsCell} 